import React, { useState } from 'react';
import { Modal, Button } from '../common';
import { Download, Trash2, AlertTriangle, ShieldCheck, Pencil, CheckCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL;

const MyDataModal = ({ show, onClose, onAccountDeleted, user }) => {
  const [step, setStep]             = useState('main');   // 'main' | 'rectify' | 'confirm_delete'
  const [loading, setLoading]       = useState(false);
  const [deleteWord, setDeleteWord] = useState('');
  const [error, setError]           = useState('');
  const [success, setSuccess]       = useState('');
  const [rectifyForm, setRectifyForm] = useState({ name: '', email: '' });

  const handleExport = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/privacy/my-data`, { credentials: 'include' });
      if (!res.ok) throw new Error('Error al exportar datos');
      const data = await res.json();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `mis-datos-prt-${new Date().toLocaleDateString('sv-SE')}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRectify = async () => {
    if (!rectifyForm.name.trim() && !rectifyForm.email.trim()) {
      setError('Debes modificar al menos un campo.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/privacy/my-data`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(rectifyForm),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.message || 'Error al actualizar datos');
      setSuccess('Datos actualizados correctamente. Recarga la página para ver los cambios.');
      setStep('main');
      setRectifyForm({ name: '', email: '' });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteWord !== 'ELIMINAR') return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/privacy/my-data`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.message || 'Error al eliminar cuenta');
      }
      onAccountDeleted();
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep('main');
    setDeleteWord('');
    setError('');
    setSuccess('');
    setRectifyForm({ name: '', email: '' });
    onClose();
  };

  return (
    <Modal
      show={show}
      onClose={handleClose}
      title="Mis Datos Personales"
      maxWidth="lg"
    >
      {step === 'main' && (
        <div className="space-y-4">
          <div className="flex gap-3 bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-3 text-sm text-slate-300">
            <ShieldCheck className="text-cyan-400 shrink-0 mt-0.5" size={18} />
            <p>
              Bajo la <strong className="text-white">Ley N° 21.719</strong> tienes derecho a acceder,
              corregir y eliminar tus datos personales almacenados en Sistema PRT.
            </p>
          </div>

          {success && (
            <div className="flex gap-2 bg-green-500/10 border border-green-500/30 rounded-lg p-3 text-sm text-green-400">
              <CheckCircle size={16} className="shrink-0 mt-0.5" />
              {success}
            </div>
          )}

          {/* Rectificar */}
          <div className="bg-slate-700 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Pencil className="text-yellow-400 shrink-0 mt-0.5" size={20} />
              <div className="flex-1">
                <p className="font-semibold text-white text-sm">Corregir mis datos</p>
                <p className="text-slate-400 text-xs mt-1">
                  Actualiza tu nombre o email registrado en el sistema.
                  {user?.name && <span className="text-slate-300"> Nombre actual: <strong>{user.name}</strong></span>}
                </p>
              </div>
            </div>
            <button
              onClick={() => { setStep('rectify'); setError(''); setRectifyForm({ name: user?.name || '', email: user?.email || '' }); }}
              className="mt-3 w-full py-2 px-4 rounded-lg bg-yellow-600/20 hover:bg-yellow-600/40 text-yellow-400 text-sm border border-yellow-500/30 transition-colors"
            >
              Corregir mis datos personales
            </button>
          </div>

          {/* Exportar */}
          <div className="bg-slate-700 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Download className="text-cyan-400 shrink-0 mt-0.5" size={20} />
              <div className="flex-1">
                <p className="font-semibold text-white text-sm">Descargar mis datos</p>
                <p className="text-slate-400 text-xs mt-1">
                  Descarga un archivo JSON con todos tus datos personales: perfil, fallas
                  asociadas, calibraciones, mantenimientos y registros de actividad.
                </p>
              </div>
            </div>
            <Button
              onClick={handleExport}
              disabled={loading}
              className="mt-3 w-full bg-cyan-700 hover:bg-cyan-600 text-white text-sm"
            >
              {loading ? 'Exportando...' : 'Descargar mis datos (JSON)'}
            </Button>
          </div>

          {/* Eliminar */}
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Trash2 className="text-red-400 shrink-0 mt-0.5" size={20} />
              <div className="flex-1">
                <p className="font-semibold text-white text-sm">Eliminar mi cuenta</p>
                <p className="text-slate-400 text-xs mt-1">
                  Anonimizamos tus datos personales (nombre, email) y desactivamos tu cuenta
                  permanentemente. Las fallas y registros de planta se mantienen con datos
                  anonimizados para integridad operacional.
                </p>
              </div>
            </div>
            <button
              onClick={() => setStep('confirm_delete')}
              className="mt-3 w-full py-2 px-4 rounded-lg bg-red-600/20 hover:bg-red-600/40 text-red-400 text-sm border border-red-500/30 transition-colors"
            >
              Solicitar eliminación de cuenta
            </button>
          </div>

          {error && (
            <p className="text-red-400 text-sm text-center">{error}</p>
          )}
        </div>
      )}

      {step === 'rectify' && (
        <div className="space-y-4">
          <p className="text-slate-300 text-sm">
            Modifica los campos que deseas corregir. Deja en blanco los que no quieras cambiar.
          </p>

          <div>
            <label className="block text-sm text-slate-300 mb-1">Nombre</label>
            <input
              type="text"
              value={rectifyForm.name}
              onChange={e => setRectifyForm(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Tu nombre completo"
              className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400"
            />
          </div>

          <div>
            <label className="block text-sm text-slate-300 mb-1">Email</label>
            <input
              type="email"
              value={rectifyForm.email}
              onChange={e => setRectifyForm(prev => ({ ...prev, email: e.target.value }))}
              placeholder="tu@email.com"
              className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400"
            />
          </div>

          {error && <p className="text-red-400 text-sm">{error}</p>}

          <div className="flex gap-3">
            <Button
              variant="secondary"
              onClick={() => { setStep('main'); setError(''); }}
              disabled={loading}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleRectify}
              disabled={loading}
              className="flex-1 bg-yellow-600 hover:bg-yellow-700 text-white"
            >
              {loading ? 'Guardando...' : 'Guardar cambios'}
            </Button>
          </div>
        </div>
      )}

      {step === 'confirm_delete' && (
        <div className="space-y-4">
          <div className="flex gap-3 bg-red-500/10 border border-red-500/40 rounded-lg p-4">
            <AlertTriangle className="text-red-400 shrink-0 mt-0.5" size={22} />
            <div>
              <p className="font-bold text-white text-sm">Esta acción es irreversible</p>
              <p className="text-slate-400 text-xs mt-1">
                Tu cuenta quedará desactivada y tus datos personales (nombre, email) serán
                anonimizados permanentemente. No podrás volver a iniciar sesión.
              </p>
            </div>
          </div>

          <div>
            <label className="block text-sm text-slate-300 mb-2">
              Para confirmar, escribe <strong className="text-white">ELIMINAR</strong> en el campo:
            </label>
            <input
              type="text"
              value={deleteWord}
              onChange={e => setDeleteWord(e.target.value)}
              placeholder="ELIMINAR"
              className="w-full px-4 py-3 bg-slate-700 border border-red-500/50 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-red-400"
            />
          </div>

          {error && (
            <p className="text-red-400 text-sm">{error}</p>
          )}

          <div className="flex gap-3">
            <Button
              variant="secondary"
              onClick={() => { setStep('main'); setDeleteWord(''); setError(''); }}
              disabled={loading}
              className="flex-1"
            >
              Cancelar
            </Button>
            <button
              onClick={handleDeleteAccount}
              disabled={loading || deleteWord !== 'ELIMINAR'}
              className="flex-1 py-2 px-4 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Eliminando...' : 'Eliminar mi cuenta'}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default MyDataModal;
